/**
 * Component Registry for Quinn Residences Theme
 * 
 * Extends the base quinn-residences theme registry with Quinn Residences specific overrides.
 */

import type { ComponentRegistry } from '@vibesites/runtime';
import { registry as baseRegistry } from '@vibesites/theme-quinn-residences/registry';
import { Hero } from './components/Hero';
import { PageHero } from './components/PageHero';
import { Footer } from './components/Footer';
import { FloorplansList } from './components/FloorplansList';
import { FloorPlansHero } from './components/FloorPlansHero';
import { FloorPlansCompare } from './components/FloorPlansCompare';
import { PropertyOverview } from './components/PropertyOverview';
import { WhyQuinn } from './components/WhyQuinn';
import { GalleryMosaic } from './components/GalleryMosaic';
import { GalleryCategoryBar } from './components/GalleryCategoryBar';
import { LifestyleHighlights } from './components/LifestyleHighlights';
import { PetPolicy } from './components/PetPolicy';
import { PetRules } from './components/PetRules';
import { AmenityHighlights } from './components/AmenityHighlights';
import { TourGallery } from './components/TourGallery';
import { ContactDetails } from './components/ContactDetails';
import { ThankYou } from './components/ThankYou';
import { VisitUs } from './components/VisitUs';
import { FaqHelp } from './components/FaqHelp';
import { Header } from './components/Header';
import { NextStep } from './components/NextStep';
import { FaqList } from './components/FaqList';
import { FaqSearch } from './components/FaqSearch';
import { FaqCategoryBar } from './components/FaqCategoryBar';
import { SpecialOfferBanner } from './components/SpecialOfferBanner';
import { AhaBand } from './components/AhaBand';
import { TourOptions } from './components/TourOptions';
import { InteractiveMapCallout } from './components/InteractiveMapCallout';
import { ReviewsList } from './components/ReviewsList';
import { EngrainSitemapEmbed } from './components/EngrainSitemapEmbed';
import { AmenitiesList } from './components/AmenitiesList';

/**
 * Quinn Residences specific component overrides.
 * These components have been customized for the Quinn Residences brand.
 * 
 * Example:
 * import { Footer } from './components/Footer';
 * 
 * const quinnresidencesOverrides: Partial<ComponentRegistry> = {
 *   Footer,
 * };
 */
const quinnresidencesOverrides: Partial<ComponentRegistry> = {
  Hero,
  PageHero,
  Footer,
  FloorplansList,
  FloorPlansHero,
  FloorPlansCompare,
  PropertyOverview,
  WhyQuinn,
  GalleryMosaic,
  GalleryCategoryBar,
  LifestyleHighlights,
  PetPolicy,
  PetRules,
  AmenityHighlights,
  TourGallery,
  ContactDetails,
  ThankYou,
  VisitUs,
  FaqHelp,
  Header,
  NextStep,
  FaqList,
  FaqSearch,
  FaqCategoryBar,
  SpecialOfferBanner,
  AhaBand,
  TourOptions,
  InteractiveMapCallout,
  ReviewsList,
  EngrainSitemapEmbed,
  AmenitiesList,
};

/**
 * Final merged registry for Quinn Residences properties.
 * Inherits all components from the quinn-residences theme, with Quinn Residences overrides.
 */
export const registry: ComponentRegistry = {
  ...baseRegistry,
  ...quinnresidencesOverrides,
};

export default registry;
